import { Character } from "@moe/priv/model/character";
import { Persona } from "@moe/priv/model/persona";
import { Names, templateNames } from "@moe/priv/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AuthModal } from "@web/components/AuthModal";
import { PersonaSelectorModal } from "@web/route-services/chat/PersonaSelector";
import { chatPreviewQueryKey } from "@web/route-services/chat/use-chat-preview-query";
import { useAppContext } from "@web/route-services/root/AppContext";
import { useCallback } from "react";
import { toast } from "sonner";

/**
 * Hook to create a new chat with one or multiple characters.
 * If there are no default personas, the user will be prompted to select one.
 */
export function useCreateChat() {
  const { access, createModal, session } = useAppContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const createChat = useCallback(
    async (chars: Character[], persona: Persona) => {
      if (!chars.length) {
        toast.error("No characters found while creating chat. This shouldn't happen sorryy, please @cyan.");
        return;
      }

      const id = await access.chat.create({
        charIDs: chars.map((char) => char.id),
        personaID: persona.id
      });

      // Template and insert the greeting message
      const char = chars[0];
      const names: Names = {
        user: persona.name,
        char: char.name
      };

      const messageID = await access.message.create({
        chatID: id,
        characterID: char.id,
        text: templateNames(char.greeting, names)
      });

      // If there are alt greetings, insert them as candidates messages
      if (char.alternativeGreetings?.length > 0) {
        await access.message.createCandidates(messageID, char.alternativeGreetings);
      }

      await queryClient.refetchQueries({ queryKey: chatPreviewQueryKey });
      navigate({ to: `/chat?chatID=${id}` });
    },
    [access.chat, access.message, navigate, queryClient]
  );

  return useCallback(
    async (characters: Character | Character[]) => {
      if (!session) {
        createModal({ content: <AuthModal formKind="signup" /> });
        return;
      }
      const chars = Array.isArray(characters) ? characters : [characters];

      const defaultPersona = await access.persona.getDefault();
      if (defaultPersona) {
        await createChat(chars, defaultPersona);
      } else {
        const config = {
          title: "Select a persona",
          content: (
            <PersonaSelectorModal
              onSelect={(persona) => {
                createChat(chars, persona);
              }}
            />
          )
        };
        createModal(config);
      }
    },
    [session, access.persona, createModal, createChat]
  );
}
